// ConsoleLogger.tsx
import React, {useEffect, useState} from 'react';
import {FR} from "WideLayout/Layouts";

interface LogEntry {
    type: 'log' | 'error' | 'warn' | 'globalError';
    message: string;
    timestamp: Date;
}

const ConsoleLogger: React.FC = () => {
    const [logs, setLogs] = useState<LogEntry[]>([]);
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        const originalLog = console.log;
        const originalError = console.error;
        const originalWarn = console.warn;

        const overrideConsole = (type: 'log' | 'error' | 'warn', originalFunc: Function) => {
            return function (...args: any[]) {
                const message = args
                    .map(arg => {
                        try {
                            return typeof arg === 'object' ? JSON.stringify(arg) : String(arg);
                        } catch (e) {
                            return String(arg);
                        }
                    })
                    .join(' ');
                setLogs(prev => [...prev, {type, message, timestamp: new Date()}]);
                originalFunc.apply(console, args);
            };
        };

        console.log = overrideConsole('log', originalLog);
        console.error = overrideConsole('error', originalError);
        console.warn = overrideConsole('warn', originalWarn);

        // Перехват глобальных ошибок с capture (для ошибок скриптов и ресурсов)
        const handleGlobalError = (event: ErrorEvent) => {
            let msg = '';
            if (event.error && event.error.message) {
                msg = event.error.message;
            } else {
                msg = event.message || 'Resource error';
            }
            setLogs(prev => [...prev, {type: 'globalError', message: `Global Error: ${msg}`, timestamp: new Date()}]);
        };
        window.addEventListener('error', handleGlobalError, true);

        // Перехват unhandled promise rejections
        const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
            const msg =
                event.reason && typeof event.reason === 'object'
                    ? JSON.stringify(event.reason)
                    : String(event.reason);
            setLogs(prev => [...prev, {
                type: 'globalError',
                message: `Unhandled Rejection: ${msg}`,
                timestamp: new Date()
            }]);
        };
        window.addEventListener('unhandledrejection', handleUnhandledRejection, true);

        return () => {
            console.log = originalLog;
            console.error = originalError;
            console.warn = originalWarn;
            window.removeEventListener('error', handleGlobalError, true);
            window.removeEventListener('unhandledrejection', handleUnhandledRejection, true);
        };
    }, []);

    if (!visible) {
        return (
            <FR rounded={3} px={1} mx={'auto'} w={'min-content'}
                onClick={() => setVisible(true)} style={{
                backgroundColor: 'rgba(82,165,255,0.51)',
                color: '#fff',
                border: 'none',
                borderRadius: '5px',
            }}>
                логи
            </FR>
        );
    }

    return (
        <div style={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            maxHeight: '50%',
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            color: '#fff',
            overflowY: 'scroll',
            zIndex: 9999,
            padding: '10px',
            fontSize: '12px',
        }}>
            <button onClick={() => setVisible(false)} style={{
                position: 'absolute',
                top: 10,
                right: 10,
                backgroundColor: '#dc3545',
                color: '#fff',
                border: 'none',
                borderRadius: '5px',
                padding: '5px 10px',
            }}>
                Закрыть
            </button>

            {logs.map((log, index) => (
                <div key={index} style={{marginBottom: '5px'}}>
                    <span>[{log.timestamp.toLocaleTimeString()}] </span>
                    <span style={{
                        color:
                            log.type === 'error' || log.type === 'globalError'
                                ? 'red'
                                : log.type === 'warn'
                                    ? 'yellow'
                                    : 'white',
                    }}
                    >{log.type.toUpperCase()}: {log.message}</span>
                </div>
            ))}
        </div>
    );
};

export default ConsoleLogger;
